import React, { useState, lazy, useRef, useMemo, useCallback } from "react";
import { Text, Title } from "@constants/Themed";
import styles from "@stylesheet";
import DefaultScreen from "@components/Global/DefaultScreenView";
import { useQuery } from "@tanstack/react-query";
import apis, { getDataFavorites, getProjectFavorites } from "@api/api";
import DeleteConfirmModal from '@components/modals/DeleteConfirmModal/DeleteConfirmModal';
import DataOptionsModal from '@components/modals/DataOptionsModal/DataOptionsModal';
import { ShareConfirmHandle } from '@components/modals/ShareUserModal/ShareUserModal';
import ActionsModal from '@components/modals/ActionsModal/ActionsModal';
import parseByDelimiter from "@utils/parseByDelimiter";
import { ActivityIndicator, FlatList, RefreshControl, View } from "react-native";
import AnimatedLottieView from "@utils/lottieAnimation";
import NetInfo from '@react-native-community/netinfo';
import {
  Card,
} from 'tamagui';
import FavouriteDataItem from "../DataScreen/components/DataItem/FavouriteDataItem";
import { filter } from "lodash";
import GlobalTutorial from "@components/TutorialComponent/GlobalTutorial";
import { carouselData } from './Tutorial/CarouselData';

const ProjectFavourites = () => {
  const [activeItem, setActiveItem] = useState(null);
  const [visible, setVisible] = useState(false);
  const [optionsState, setOptionsState] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [carouselProjectData, setCarouselProjectData] = useState(carouselData); 
  const shareUserRef = useRef<ShareConfirmHandle>(null);
  const ShareUserModal = lazy(() => import('@components/modals/ShareUserModal/ShareUserModal'));
  const [requestActive, isRequestActive] = useState(false);
  const [query, setQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
//TODO Map companyId and UserId to global variables for application;
    const [listHorizontal, setListHorizontal] = useState(true);
    const [retrievedData, setRetrievedData] = useState([]);

    const { status, data: favouriteData, error, isFetching, isLoading, refetch } = useQuery({
      queryKey: ['favouriteData'],
      queryFn: async () => {
        const data = await getProjectFavorites()
          .then(res => {
            return res
          });
        setRetrievedData(data);
        return data
      },
    })

    const handleRefresh = useCallback(async () => {
      const isConnected = await NetInfo.fetch().then(state => state.isConnected);
      if (isConnected) {
        setRefreshing(true);
        refetch().then(() => setRefreshing(false));
      } else {
        alert('No internet connection found, please try again later');
      }
    }, []);

    const _handleSettingsModal = useCallback((item) => {
      setActiveItem(item);
      setOptionsState(true);
    }, [activeItem]);


    const contains = ({ dataTitle, dataTags }, query) => {
      query = query.replace(/ /g, '_'); // Replace spaces with underscores
      if (dataTitle.toLowerCase().includes(query) || dataTags.includes(query)) {
        return true;
      }
      return false;
    };

    const handleSearch = text => {
      const formattedQuery = text.toLowerCase();
      if (!formattedQuery) { return setRetrievedData(favouriteData); }
      const filteredData = filter(favouriteData, item => {
        return contains(item, formattedQuery);
      });
      setRetrievedData(filteredData);
    };

    const _handleActionModal = useCallback((item) => {
      setActiveItem(item);
      setOptionsState(true);
    }, [activeItem]);

    const _handleDeleteModal = useCallback((item) => {
      setActiveItem(item);
      setVisible(true);
    }, [activeItem]);

    const _handleShareState = useCallback((item) => {
      setActiveItem(item);
      shareUserRef.current?.openModal(item);
    }, [activeItem, shareUserRef])

    const onRefresh = React.useCallback(() => {
      handleRefresh()
    }, []);



    return (
      <DefaultScreen backButton style={{ paddingTop: 10 }} searchBar={{}}>
        <DeleteConfirmModal deleteFunction={apis.deleteDataItem} itemId={activeItem?._id} title={parseByDelimiter(activeItem?.dataTitle || "")} refreshFunction={handleRefresh} setState={setVisible} visibleState={visible} />
        <DataOptionsModal setState={setOptionsState} visibleState={optionsState} item={activeItem} title={parseByDelimiter(activeItem?.dataTitle || "")} refreshFunction={handleRefresh} />
        <ShareUserModal ref={shareUserRef} item={activeItem} shareFunction={apis.shareDataItem} />
        <ActionsModal setState={setShowActionModal} visibleState={showActionModal} item={activeItem} />
        <View style={{ flex: 1, paddingRight: 10, paddingTop: 20 }}>
          <React.Suspense fallback={<View style={{ flex: 1 }}><AnimatedLottieView autoPlay source={require('@assets/animations/leaf_Loader.json')} /></View>}>
            <FlatList
              key={'dataFavourites'}
              data={favouriteData}
              keyExtractor={(item) => item._id}
              ListEmptyComponent={(
                <View style={{ flex: 1, alignSelf: 'center', alignContent: 'center', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {query != '' ?
                    <View style={[styles.noteContainer, { flex: 1, margin: 50, padding: 5, paddingBottom: 20 }]}>
                      <AnimatedLottieView source={require('@assets/animations/88006-warning-yellow.json')} autoPlay loop={false} style={{ height: 100, width: 100, flex: 1 }} />
                      <Text style={{ flex: 1 }}>No items found matching query</Text>
                    </View>
                    :
                    <View style={{ gap: 30, margin: 30 }}>
                      <View style={[styles.centerEverything, { gap: 30, flex: 1, maxWidth: "100%", alignSelf: "stretch" }]}>
                        {
                          isFetching || isLoading &&
                          <ActivityIndicator size="large" color="white" />
                        }
                        <GlobalTutorial customHeight={350} customWidth={300} carouselData={carouselProjectData} />
                      </View>
                    </View>
                  }
                </View>
              )}
            contentContainerStyle={{ paddingBottom: 50 }}
            renderItem={({ item, index }) => (<FavouriteDataItem handleShareModal={_handleShareState} handleSettingsModal={_handleSettingsModal} handleDeleteModal={_handleDeleteModal} handleActionModal={_handleActionModal} handleRefresh={handleRefresh} item={item} index={index} handleShareState={_handleShareState} listHorizontal={listHorizontal} />)}
            showsVerticalScrollIndicator={false}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
            style={{ flex: 1, marginRight: -20 }}
            getItemLayout={(data, index) => ({ length: 350, offset: 350 * index, index })}
            ListFooterComponentStyle={{ alignSelf: 'center', maxWidth: '100%', justifyContent: 'center', paddingBottom: 50 }}
          />
        </React.Suspense>
      </View>
    </DefaultScreen>
  );
}
export default ProjectFavourites;